import { getPlatform } from '../platform/registry.ts';

// Single network entrypoint. Every call routes through here so the
// credential header injection lives in one place. The bot's
// TelegramInitDataGuard accepts either x-session-token (preferred, 3h TTL)
// or x-telegram-init-data (5-min freshness window, used only to obtain
// a session token on first load).

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Thrown when the server-side session token is absent or past its 6-hour
// TTL. The FE must exchange a fresh initData for a new token — this
// happens automatically via ensureSessionToken(); only surfaces to the
// user if Telegram.WebApp.initData itself has aged past 5 minutes (i.e.
// they must reopen the miniapp).
export class AuthStaleError extends ApiError {
  constructor(message = 'Session expired') {
    super(401, message);
    this.name = 'AuthStaleError';
  }
}

export const SESSION_EXPIRED_EVENT = 'miniapp:session-expired';

export class SessionTerminatedError extends ApiError {
  constructor(code: string, message: string) {
    super(403, message, code);
    this.name = 'SessionTerminatedError';
  }
}

export const SESSION_TERMINATED_EVENT = 'miniapp:session-terminated';

// The auth-exchange 403 codes that mean the session never opened, as opposed
// to a token that aged out (AUTH_STALE / SESSION_EXPIRED). Owned here because
// this is where the wire codes enter the app; the /logged-out splash imports
// this list to pick its copy, so the two can never drift to different sets.
export const SESSION_TERMINATED_CODES = [
  'ACCOUNT_INACTIVE',
  'ACCOUNT_NOT_LINKED',
  'ACCOUNT_NOT_FOUND',
] as const;

export type SessionTerminatedCode = (typeof SESSION_TERMINATED_CODES)[number];

export const isSessionTerminatedCode = (value: unknown): value is SessionTerminatedCode =>
  typeof value === 'string' && (SESSION_TERMINATED_CODES as readonly string[]).includes(value);

// The shape the api client puts on SESSION_TERMINATED_EVENT, exported so the
// listener narrows against the real producer instead of a hand-copied twin.
export interface SessionTerminatedEventDetail {
  readonly code: string;
}

export class KycRequiredError extends ApiError {
  constructor(
    public readonly kycStatus: KycStatusEnvelope,
    message: string,
  ) {
    super(403, message);
    this.name = 'KycRequiredError';
  }
}

export const KYC_REQUIRED_EVENT = 'miniapp:kyc-required';

// In-memory session token cache. Keyed by expiry so we can proactively
// re-exchange before the server rejects the request.
interface SessionTokenEntry {
  readonly token: string;
  readonly expiresAt: number; // ms since epoch
}

let sessionTokenCache: SessionTokenEntry | null = null;

// Returns true if the cached token is still valid with a 60-second buffer
// so we rotate slightly before expiry rather than letting a request fail.
const isTokenValid = (entry: SessionTokenEntry): boolean => entry.expiresAt - Date.now() > 60_000;

// In-flight exchange promise — coalesces concurrent calls so we don't
// issue multiple POST /auth requests in parallel on the first load.
let exchangeInFlight: Promise<string> | null = null;

const exchangeToken = async (): Promise<string> => {
  const initData = getPlatform().initData();
  if (initData === '') {
    // Outside Telegram WebView (dev browser) — no token available.
    throw new AuthStaleError('No Telegram initData available');
  }
  const res = await fetch('/api/miniapp/auth', {
    method: 'POST',
    headers: { 'X-Telegram-Init-Data': initData },
  });
  if (!res.ok) {
    if (res.status === 401) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      throw new AuthStaleError('initData expired — reopen the miniapp');
    }
    // A logged-out / unlinked / inactive account 403s here at the very first
    // token exchange, before any guarded request runs. Without this branch the
    // 403 surfaced as a generic error and the UI offered a pointless Retry;
    // route it to the logged-out splash instead, which tells the user to go
    // back to the bot and /start.
    let code: string | undefined;
    try {
      const errBody = (await res.json()) as { code?: unknown };
      if (typeof errBody.code === 'string') code = errBody.code;
    } catch {
      /* non-JSON error body — fall through to the generic error */
    }
    if (res.status === 403 && isSessionTerminatedCode(code)) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_TERMINATED_EVENT, { detail: { code } }));
      }
      throw new SessionTerminatedError(code, 'Session ended — return to the bot and /start');
    }
    throw new ApiError(res.status, `Auth exchange failed: ${String(res.status)}`, code);
  }
  const body = (await res.json()) as { sessionToken: string; expiresAt: string };
  sessionTokenCache = {
    token: body.sessionToken,
    expiresAt: new Date(body.expiresAt).getTime(),
  };
  return body.sessionToken;
};

// Returns a valid session token, exchanging via initData if needed.
// Coalesces concurrent callers so only one exchange request is in flight.
const ensureSessionToken = (): Promise<string> => {
  if (sessionTokenCache !== null && isTokenValid(sessionTokenCache)) {
    return Promise.resolve(sessionTokenCache.token);
  }
  if (exchangeInFlight !== null) return exchangeInFlight;
  exchangeInFlight = exchangeToken().finally(() => {
    exchangeInFlight = null;
  });
  return exchangeInFlight;
};

// Clears the cached token — called on logout so the next request forces
// a fresh exchange (which will fail, directing the user to reopen).
export const clearSessionToken = (): void => {
  sessionTokenCache = null;
};

interface ApiOptions {
  readonly method?: 'GET' | 'POST' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  // A second-factor confirmation, obtained by running a step-up ceremony and
  // bound server-side to this exact operation. Presented once: the bot spends
  // it before the handler runs, so a retry needs a fresh one.
  readonly stepUpGrant?: string;
}

export const STEP_UP_GRANT_HEADER = 'X-Step-Up-Grant';

export async function api<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const sessionToken = await ensureSessionToken();

  const headers: Record<string, string> = {
    'X-Session-Token': sessionToken,
  };
  if (opts.stepUpGrant !== undefined) headers[STEP_UP_GRANT_HEADER] = opts.stepUpGrant;
  let body: string | undefined;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const fetchInit: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
  };
  if (body !== undefined) fetchInit.body = body;
  if (opts.signal !== undefined) fetchInit.signal = opts.signal;

  const res = await fetch(`/api/miniapp${path}`, fetchInit);
  if (!res.ok) {
    let msg = `${String(res.status)} ${res.statusText}`;
    let code: string | undefined;
    let kycStatus: KycStatusEnvelope | undefined;
    let retryAfter: number | undefined;
    try {
      const errBody = (await res.json()) as {
        code?: unknown;
        message?: unknown;
        kycStatus?: unknown;
        retryAfter?: unknown;
      };
      if (typeof errBody.message === 'string') msg = errBody.message;
      if (typeof errBody.code === 'string') code = errBody.code;
      if (isKycStatusEnvelope(errBody.kycStatus)) kycStatus = errBody.kycStatus;
      if (typeof errBody.retryAfter === 'number') retryAfter = errBody.retryAfter;
    } catch {
      // body was not JSON — keep the status-line message
    }

    // Session token expired server-side — clear cache and retry once with
    // a freshly exchanged token. If the exchange itself fails (initData
    // also expired) the retry throws AuthStaleError which propagates up.
    if (res.status === 401 && (code === 'SESSION_EXPIRED' || code === 'SESSION_INVALID')) {
      clearSessionToken();
      try {
        const freshToken = await exchangeToken();
        const retryHeaders: Record<string, string> = { 'X-Session-Token': freshToken };
        if (opts.stepUpGrant !== undefined) retryHeaders[STEP_UP_GRANT_HEADER] = opts.stepUpGrant;
        if (body !== undefined) retryHeaders['Content-Type'] = 'application/json';
        const retryInit: RequestInit = { method: opts.method ?? 'GET', headers: retryHeaders };
        if (body !== undefined) retryInit.body = body;
        if (opts.signal !== undefined) retryInit.signal = opts.signal;
        const retryRes = await fetch(`/api/miniapp${path}`, retryInit);
        if (retryRes.ok) return (await retryRes.json()) as T;
        // Retry also failed — fall through to normal error handling below
        // by re-parsing the retry response. Simplest: just throw stale.
      } catch {
        // exchangeToken already dispatched SESSION_EXPIRED_EVENT if initData expired
        throw new AuthStaleError(msg);
      }
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      throw new AuthStaleError(msg);
    }

    if (res.status === 401 && code === 'AUTH_STALE') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
      throw new AuthStaleError(msg);
    }
    if (res.status === 403 && isSessionTerminatedCode(code)) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_TERMINATED_EVENT, { detail: { code } }));
      }
      throw new SessionTerminatedError(code, msg);
    }
    if (res.status === 403 && code === 'KYC_REQUIRED') {
      const status: KycStatusEnvelope = kycStatus ?? 'UNKNOWN';
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent(KYC_REQUIRED_EVENT, { detail: { kycStatus: status } }),
        );
      }
      throw new KycRequiredError(status, msg);
    }
    throw new ApiError(res.status, msg, code, retryAfter);
  }
  return (await res.json()) as T;
}

const isKycStatusEnvelope = (value: unknown): value is KycStatusEnvelope =>
  value === 'NOT_STARTED' ||
  value === 'IN_PROGRESS' ||
  value === 'PASSED' ||
  value === 'REJECTED' ||
  value === 'UNKNOWN';

// Domain models mirror BackendApiPort shapes on the bot.
export interface CardSummary {
  readonly id: string;
  readonly status?: string;
  readonly type?: string;
  readonly lastFour?: string;
  readonly cardLabel?: string;
  readonly balance?: string;
  readonly currency?: string;
  readonly physicalCardStatus?: string;
  readonly physicalActivatedAt?: string | null;
}

export interface CardTransaction {
  readonly id: string;
  readonly amount: string;
  readonly currency: string;
  readonly status: string;
  readonly createdAt: string;
  readonly description?: string;
  // The card backend reports a unified transaction shape. `type` is the only
  // reliable direction signal: card spends arrive as positive magnitudes, so
  // the +/- sign cannot be read off `amount`. `details` carries the merchant
  // descriptor (the field the list actually shows). `merchantAmount` /
  // `merchantCurrency` carry the original-currency leg of a foreign-currency
  // purchase, rendered as a sub-amount.
  readonly type?: 'TRANSACTION' | 'TOP_UP' | 'COMMISSION';
  readonly details?: string;
  readonly merchantAmount?: string;
  readonly merchantCurrency?: string;
  // A transaction that never went through — a declined authorization — settles
  // at zero, so `amount` and `merchantAmount` are both '0' and say nothing
  // about what was attempted. These carry the presented amount
  // (`attemptedAmount` in the card's currency, signed; `attemptedMerchantAmount`
  // a magnitude in `merchantCurrency`) and the provider's refusal. Either
  // attempted leg may be absent: a declined foreign-currency authorization is
  // never priced, so it has no honest card-currency figure. Display-only —
  // these must never be added into a total, because no money moved.
  readonly attemptedAmount?: string;
  readonly attemptedMerchantAmount?: string;
  readonly declineReason?: string;
  readonly declineCode?: string;
}

export interface TransactionsPage {
  readonly data: readonly CardTransaction[];
  readonly page: number;
  readonly limit: number;
  readonly total?: number;
}

export interface CryptoCurrency {
  readonly id: string;
  readonly code: string;
  readonly symbol: string;
  readonly name: string;
  readonly network: string;
  readonly standard?: string;
  readonly minimumAmount: string;
  readonly rate?: number;
}

export interface DepositDetails {
  readonly address: string;
  readonly qrCode?: string;
  readonly minimumAmount: string;
  readonly fee?: number;
  readonly rate?: number;
}

export interface CardPanUrl {
  readonly accessUrl: string;
}

export type KycStatusEnvelope = 'NOT_STARTED' | 'IN_PROGRESS' | 'PASSED' | 'REJECTED' | 'UNKNOWN';

export interface AccountProfile {
  readonly accountIdMasked: string;
  readonly email: string;
  readonly name: string;
  readonly kycStatus: KycStatusEnvelope;
  readonly memberSince: string;
  readonly role?: string;
  readonly accessStatus?: string;
}

export interface CardShipment {
  readonly status: string;
  readonly trackingNumber: string | null;
  readonly trackingUrl: string | null;
  readonly courierName: string | null;
}

export const listCards = (
  signal?: AbortSignal,
): Promise<{ readonly cards: readonly CardSummary[] }> =>
  api('/cards', signal !== undefined ? { signal } : {});

export const getCardShipment = (
  cardId: string,
  signal?: AbortSignal,
): Promise<CardShipment | null> =>
  api<CardShipment | null>(
    `/cards/${encodeURIComponent(cardId)}/shipment`,
    signal !== undefined ? { signal } : {},
  );

export const CARD_TRANSACTIONS_PAGE_SIZE = 20;

export const getCardTransactions = (
  cardId: string,
  page: number,
  limit: number = CARD_TRANSACTIONS_PAGE_SIZE,
  signal?: AbortSignal,
): Promise<TransactionsPage> =>
  api(
    `/cards/${encodeURIComponent(cardId)}/transactions?page=${String(page)}&limit=${String(limit)}`,
    signal !== undefined ? { signal } : {},
  );

export const listCurrencies = (
  signal?: AbortSignal,
): Promise<{ readonly currencies: readonly CryptoCurrency[] }> =>
  api('/crypto/currencies', signal !== undefined ? { signal } : {});

export const getTopupDetails = (cardId: string, currencyId: string): Promise<DepositDetails> =>
  api('/topup/details', { method: 'POST', body: { cardId, currencyId } });

export interface TopupFromVaultResult {
  readonly txHash?: string;
  readonly status: string;
  readonly requestId: string;
  readonly idempotent?: boolean;
}

// Moves the user's Vault funds to the card top-up address. The bot resolves the
// authoritative destination and currency server-side; idempotencyKey guards
// against a double-send on retry.
export const topUpFromVault = (
  cardId: string,
  currencyId: string,
  amount: string,
  idempotencyKey: string,
): Promise<TopupFromVaultResult> =>
  api('/topup/from-vault', {
    method: 'POST',
    body: { cardId, currencyId, amount, idempotencyKey },
  });

export const getCardPan = (cardId: string, stepUpGrant?: string): Promise<CardPanUrl> =>
  api(
    `/cards/${encodeURIComponent(cardId)}/pan`,
    stepUpGrant !== undefined ? { stepUpGrant } : {},
  );

export const freezeCard = (cardId: string): Promise<{ readonly ok: true }> =>
  api(`/cards/${encodeURIComponent(cardId)}/freeze`, { method: 'POST' });

export const unfreezeCard = (cardId: string): Promise<{ readonly ok: true }> =>
  api(`/cards/${encodeURIComponent(cardId)}/unfreeze`, { method: 'POST' });

export const getAccount = (signal?: AbortSignal): Promise<AccountProfile> =>
  api('/account', signal !== undefined ? { signal } : {});

export interface BotInfo {
  readonly username: string;
  // Direct t.me link to the support chat, so the Mini App can open support
  // without bouncing through the bot's /start=support message.
  readonly supportUrl: string;
  // Whether the wallet feature is on for this deployment. When false the FE
  // hides the Vault entry point (its endpoints 404 server-side anyway).
  readonly walletEnabled: boolean;
}

export const getBotInfo = (signal?: AbortSignal): Promise<BotInfo> =>
  api('/bot-info', signal !== undefined ? { signal } : {});

// Tokenless read of /bot-info. The endpoint is public (no init-data guard),
// so this skips the session-token exchange that api() performs — needed on
// the logged-out splash, where the user's session is gone and the exchange
// would 403 before bot-info could be read.
export const fetchBotInfoPublic = async (signal?: AbortSignal): Promise<BotInfo> => {
  const res = await fetch('/api/miniapp/bot-info', signal !== undefined ? { signal } : {});
  if (!res.ok) throw new ApiError(res.status, `bot-info failed: ${String(res.status)}`);
  return (await res.json()) as BotInfo;
};

export type KycStartResponse =
  | { readonly kind: 'PASSED' }
  | { readonly kind: 'URL'; readonly url: string }
  | { readonly kind: 'BLOCKED'; readonly reason: 'FAILED' | 'ORPHANED' | 'MIGRATED' };

export const startKyc = (): Promise<KycStartResponse> => api('/kyc/start', { method: 'POST' });

export const logout = (): Promise<{ readonly ok: true }> => api('/logout', { method: 'POST' });

export type CardType = 'VIRTUAL' | 'PHYSICAL';

export interface KycDetails {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly residentialAddressLine1: string | null;
  readonly residentialAddressLine2: string | null;
  readonly residentialAddressCity: string | null;
  readonly residentialAddressPostalCode: string | null;
  readonly residentialAddressCountry: string | null;
}

export const getKycDetails = (signal?: AbortSignal): Promise<KycDetails> =>
  api('/kyc/details', signal !== undefined ? { signal } : {});

export interface UserProfile {
  readonly email: string;
  readonly phoneDialCode?: string;
  readonly phoneNumber?: string;
}

export const getUserProfile = (signal?: AbortSignal): Promise<UserProfile> =>
  api('/user/profile', signal !== undefined ? { signal } : {});

export interface ShippingInfo {
  readonly shippingAddress: {
    readonly line1: string;
    readonly line2?: string;
    readonly zone?: string;
    readonly city: string;
    readonly postalCode: string;
    readonly country: string;
  };
  readonly recipientFirstName: string;
  readonly recipientLastName: string;
  readonly recipientPhoneNumber: string;
  readonly recipientPhoneDialCode: number;
  readonly recipientEmail: string;
  readonly courier?: {
    readonly type: string;
    readonly name?: string;
  };
}

export interface CreateCardRequest {
  readonly type?: CardType;
  readonly name?: string;
  readonly dialCode: string;
  readonly phoneNumber: string;
  readonly shipping?: ShippingInfo;
}

export interface CreateCardResult {
  readonly id: string;
  readonly type: string;
  readonly status: string;
  readonly last4: string;
  readonly shippingError?: { readonly code: string; readonly message: string };
}

export const createCard = (body: CreateCardRequest): Promise<CreateCardResult> =>
  api('/cards', { method: 'POST', body });

export const updateCardPin = (
  cardId: string,
  pin: string,
  stepUpGrant?: string,
): Promise<{ readonly ok: true }> =>
  api(`/cards/${encodeURIComponent(cardId)}/pin`, {
    method: 'POST',
    body: { pin },
    ...(stepUpGrant !== undefined && { stepUpGrant }),
  });

export const shipCard = (cardId: string, body: ShippingInfo): Promise<{ readonly ok: true }> =>
  api(`/cards/${encodeURIComponent(cardId)}/ship`, { method: 'POST', body });
// ============================================================
// Wallet (Crypto Vault)
// Append this block to the end of src/api/client.ts
// ============================================================

export interface WalletBalance {
  readonly currency: string; // e.g. 'TRX_USDT'
  readonly amount: string; // decimal string from microservice, e.g. '21.000000'
  readonly address?: string; // resolved deposit address
  readonly hasAddress?: boolean;
  // Value of this balance per fiat code, e.g. { USD: '1200.00', EUR: '1104.00' }
  readonly denominated?: Record<string, string>;
}

export interface WalletBalancesResponse {
  readonly balances: readonly WalletBalance[];
}

export interface WalletAddressResponse {
  readonly currency: string;
  readonly address: string;
  readonly network?: string;
  readonly qrCode?: string; // base64 data URL or SVG string from provider
  readonly expiresAt?: string; // ISO timestamp if the address has a TTL
}

export interface WalletFeeResponse {
  readonly currency: string;
  readonly fee: string; // decimal string, e.g. '0.000025'
  readonly feeCurrency: string; // e.g. 'TRX' for TRX_USDT sends
  readonly feeUsd?: string; // optional USD equivalent
}

export interface WalletSendResponse {
  readonly txHash?: string;
  readonly status: string; // 'PENDING' | 'COMPLETED' | 'FAILED'
  readonly requestId?: string;
}

export interface WalletTx {
  readonly id: string;
  readonly currency: string;
  readonly direction: 'SEND' | 'RECEIVE';
  readonly amount: string;
  readonly fee?: string;
  readonly txHash?: string;
  readonly status: string; // 'PENDING' | 'COMPLETED' | 'FAILED'
  readonly createdAt: string; // ISO timestamp
  readonly fromAddress?: string;
  readonly toAddress?: string;
}

export interface WalletTxPage {
  readonly txs: readonly WalletTx[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export const getWalletBalances = (signal?: AbortSignal): Promise<WalletBalancesResponse> =>
  api('/wallet/balances', signal !== undefined ? { signal } : {});

export const getWalletAddress = (
  currency: string,
  signal?: AbortSignal,
): Promise<WalletAddressResponse> =>
  api(`/wallet/address/${encodeURIComponent(currency)}`, signal !== undefined ? { signal } : {});

export const getWalletFee = (currency: string, signal?: AbortSignal): Promise<WalletFeeResponse> =>
  api(`/wallet/fee/${encodeURIComponent(currency)}`, signal !== undefined ? { signal } : {});

export const sendWallet = (
  currency: string,
  toAddress: string,
  amount: string,
  stepUpGrant?: string,
): Promise<WalletSendResponse> =>
  api('/wallet/send', {
    method: 'POST',
    body: { currency, toAddress, amount },
    ...(stepUpGrant !== undefined && { stepUpGrant }),
  });

export const WALLET_TRANSACTIONS_PAGE_SIZE = 20;

export const getWalletTransactions = (
  currency: string,
  page: number,
  signal?: AbortSignal,
  pageSize: number = WALLET_TRANSACTIONS_PAGE_SIZE,
): Promise<WalletTxPage> =>
  api(
    `/wallet/transactions/${encodeURIComponent(currency)}?page=${String(page)}&pageSize=${String(pageSize)}`,
    signal !== undefined ? { signal } : {},
  );
  
// ============================================================
// Support agent (Level 1 AI help)
// ============================================================

export interface SupportTurn {
  readonly role: 'user' | 'assistant';
  readonly content: string;
}

export const sendSupportMessage = (
  messages: readonly SupportTurn[],
): Promise<{ readonly reply: string }> =>
  api('/support/message', { method: 'POST', body: { messages } });